import { PayloadAction, createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { SLICE_NAME } from "../_enums/state.enum";
import { ICartInitState } from "../_interfaces/state.interface";
import {
  INewProductInCart,
  IProductInCart,
} from "../_interfaces/product.interface";
import { CartHelper } from "src/_shares/_helpers/cart.helper";
import { remove, sumBy } from "lodash";
import ProductService from "../_services/product.service";

const initialState: ICartInitState = {
  vouchers: [],
  productsInCart: [],
  totalQty: 0,
  totalPrice: 0,
  totalDiscount: 0,
  totalFinalPrice: 0,
};

const calculateTotalPrice = (productsInCart: IProductInCart[], vouchers: any[] = []): ICartInitState => {
  productsInCart.forEach(_product => {
    let _finalPrice = (_product.discount ?? _product.price) * _product.qty;
    const vouchersForProduct = vouchers.filter((voucher: any) => CartHelper.isVoucherApplicable(voucher, _product));
    const discountValue = sumBy(vouchersForProduct, (voucher: any) => {
      return voucher.isPercent
        ? (_finalPrice * voucher.value) / 100
        : voucher.value
    });
    _product.discountFromVoucher = discountValue;
    _product.finalPrice = (_finalPrice - discountValue) > 0
      ? (_finalPrice - discountValue)
      : 0;
  });
  return {
    vouchers: vouchers,
    productsInCart,
    totalQty: sumBy(productsInCart, "qty"),
    totalPrice: sumBy(
      productsInCart,
      (_product) => (_product.discount ?? _product.price) * _product.qty
    ),
    totalDiscount: sumBy(
      productsInCart,
      (_product) => _product.discountFromVoucher
    ),
    totalFinalPrice: sumBy(
      productsInCart,
      (_product) => _product.finalPrice
    ),
  }
}

export const updateProductPrice = createAsyncThunk(
  'data/updateProductPrice',
  async (ids: number[]) => {
    const _params = {
      ids: ids
    };
    const response = await ProductService.getProductList(_params);
    return response.data?.items;
  }
);

const cartSlice = createSlice({
  name: SLICE_NAME.CART,
  initialState: initialState,
  reducers: {
    clearCart: (state) => {
      state.vouchers = [];
      state.totalPrice = 0;
      state.totalQty = 0;
      state.totalDiscount = 0;
      state.totalFinalPrice = 0;
      state.productsInCart = [];
    },
    addToCart: (state, action: PayloadAction<INewProductInCart>) => {
      const _product = action.payload;
      const _uniqId = CartHelper.getUniqId(_product);
      const idx = state.productsInCart.findIndex((_) => _.uniqId === _uniqId);
      if (idx > -1) {
        state.productsInCart[idx].qty += _product.qty;
        state.productsInCart[idx].uniqId = CartHelper.getUniqId(
          state.productsInCart[idx]
        );
      } else {
        const _productInCart = {
          ..._product,
          uniqId: _uniqId,
          finalPrice: (_product.discount ?? _product.price) * _product.qty // default
        } as IProductInCart;
        state.productsInCart.push(_productInCart);
      }
      Object.assign(state, calculateTotalPrice(state.productsInCart, state.vouchers ?? []));
    },
    removeItem: (state, action: PayloadAction<string>) => {
      const _uniqId = action.payload;
      const idx = state.productsInCart.findIndex((_) => _.uniqId === _uniqId);
      if (idx > -1) {
        state.productsInCart = state.productsInCart.filter((_) => _.uniqId !== _uniqId);
        Object.assign(state, calculateTotalPrice(state.productsInCart, state.vouchers));
      }
    },
    changeItemQty: (state, action: PayloadAction<{ uniqId: string, qty: number }>) => {
      const { uniqId, qty } = action.payload;
      const idx = state.productsInCart.findIndex((_) => _.uniqId === uniqId);
      if (idx > -1) {
        // Per product
        state.productsInCart[idx].qty = qty;
        Object.assign(state, calculateTotalPrice(state.productsInCart, state.vouchers));
      }
    },
    applyDiscount: (state, action: PayloadAction<any>) => {
      const discountItem = action.payload;
      const idx = state.vouchers.findIndex((_: any) => _.voucher === discountItem.voucher);
      if (idx === -1) {
        // If voucher already applied, do nothing
        state.vouchers.push(discountItem);
      } else {
        state.vouchers[idx] = discountItem;
      }
      Object.assign(state, calculateTotalPrice(state.productsInCart, state.vouchers));
    },
    removeDiscount: (state, action: PayloadAction<string>) => {
      const _voucher = action.payload;
      const idx = state.vouchers.findIndex((_) => _.voucher === _voucher);
      if (idx > -1) {
        state.vouchers = state.vouchers.filter((_) => _.voucher !== _voucher);
        Object.assign(state, calculateTotalPrice(state.productsInCart, state.vouchers));
      }
    },
    clearDiscount: (state) => {
      state.vouchers = [];
      Object.assign(state, calculateTotalPrice(state.productsInCart, state.vouchers));
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateProductPrice.fulfilled, (state, action) => {
        const products = action.payload;
        state.productsInCart.forEach(_product => {
          const idx = products.findIndex((_: any) => _.id === _product.id);
          if (idx > -1) {
            if (_product.priceType === 'AVAILABLE') {
              _product.price = products[idx].priceAvailable;
              _product.discount = products[idx].discountAvailable;
            }
            if (_product.priceType === 'PREORDER') {
              _product.price = products[idx].pricePreOrder;
              _product.discount = products[idx].discountPreOrder;
            }
            _product.finalPrice = ((_product.discount ?? _product.price) * _product.qty) - (_product.discountFromVoucher || 0);
          }
        });
        Object.assign(state, calculateTotalPrice(state.productsInCart, state.vouchers));
      })
  }
});

export const { addToCart, removeItem, changeItemQty, clearCart, applyDiscount, clearDiscount, removeDiscount } = cartSlice.actions;

const cartReducer = cartSlice.reducer;
export default cartReducer;
